import { useEffect, useMemo, useRef, useState } from 'react'
import { getWorkflowLive } from './api'

// Map raw events into simplified structure
function normalizeEvent(ev) {
  const type = ev.type || ev?.type || ''
  return {
    id: ev.id || String(Math.random()),
    type,
    t: ev.t,
    next_agent: ev.next_agent,
    reason: ev.reason,
    agent: ev.agent,
    payload: ev,
  }
}

export default function useWorkflowLive(workflowId) {
  const [status, setStatus] = useState(null)
  const [events, setEvents] = useState([])
  const [snapshot, setSnapshot] = useState(null)
  const seen = useRef(new Set())
  const timerRef = useRef(null)
  const hrSeenRef = useRef(false)
  const [loading, setLoading] = useState(true)
  const [showLoading, setShowLoading] = useState(false)

  // Delayed loading indicator - only show after 500ms to avoid flicker
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowLoading(true), 500)
      return () => clearTimeout(timer)
    } else {
      setShowLoading(false)
    }
  }, [loading])

  // Single atomic poll for status + events + snapshot
  useEffect(() => {
    if (!workflowId) {
      setStatus(null)
      setEvents([])
      setSnapshot(null)
      setLoading(true)
      seen.current = new Set()
      hrSeenRef.current = false
      try {
        clearInterval(timerRef.current)
      } catch {}
      return
    }
    let mounted = true
    // Reset per-workflow trackers
    seen.current = new Set()
    hrSeenRef.current = false
    setLoading(true)

    // Atomic fetch function
    const fetchLive = async () => {
      try {
        const data = await getWorkflowLive(workflowId, 500)
        if (!mounted) return
        // Update status
        setStatus(data.status)
        // Update events (dedupe by id)
        const incoming = (data.events || []).slice().reverse().map(normalizeEvent)
        setEvents(prev => {
          const next = [...prev]
          for (const ev of incoming) {
            if (!seen.current.has(ev.id)) {
              seen.current.add(ev.id)
              next.push(ev)
            }
          }
          return next
        })
        // Update snapshot
        setSnapshot(data.snapshot)
        setLoading(false)
        // Stop polling when done
        if (data.status?.done) {
          clearInterval(timerRef.current)
        }
      } catch (e) {
        try {
          console.warn('[flow] live poll failed', e)
        } catch {}
      }
    }

    // Prime immediately
    fetchLive()
    // Poll every 2.5 seconds
    timerRef.current = setInterval(fetchLive, 2500)

    return () => {
      mounted = false
      clearInterval(timerRef.current)
    }
  }, [workflowId])

  // Derive nodes and edges from events
  const { nodes, edges, currentStep } = useMemo(() => {
    // Track if human review ever existed in this workflow
    try {
      if (snapshot?.pending_human_request) hrSeenRef.current = true
      for (const ev of events || []) {
        if (ev.type === 'paused' || ev.type === 'human_review_requested' || ev.type === 'human_review_resolved') {
          hrSeenRef.current = true
          break
        }
      }
    } catch {}

    // Edges still derived from router decisions (optional for future graph)
    const orderDynamic = []
    const edges = []
    let last = 'start'
    for (const ev of events) {
      if (ev.type === 'router_decision') {
        const next = ev.next_agent
        if (next) {
          // Build dynamic path
          edges.push({ from: last, to: next })
          if (!orderDynamic.includes(next)) orderDynamic.push(next)
          last = next
        }
      }
      if (ev.type === 'paused') {
        if (!orderDynamic.includes('human_review')) orderDynamic.push('human_review')
      }
    }
    const stepSet = new Set(orderDynamic)
    // Create nodes with statuses from latest events
    const statusByStep = new Map()
    const attemptByStep = new Map()
    const errorByStep = new Map()
    // Derive attempts/errors from snapshot.history
    try {
      const hist = snapshot?.history || []
      for (const rec of hist) {
        const a = rec.agent
        if (!a) continue
        const at = Number(rec.attempt || 0)
        attemptByStep.set(a, Math.max(attemptByStep.get(a) || 0, at))
        if (rec.ok === false || rec.error) {
          errorByStep.set(a, (errorByStep.get(a) || 0) + 1)
        }
      }
    } catch {}
    for (const ev of events) {
      if (ev.type === 'router_decision') {
        statusByStep.set(ev.next_agent, 'running')
      }
      if (ev.type === 'agent_start' && ev.agent) {
        statusByStep.set(ev.agent, 'running')
      }
      if (ev.type === 'paused') statusByStep.set('human_review', 'paused')
      if (ev.type === 'failed' || ev.type === 'routing_error') {
        const lastNode = orderDynamic[orderDynamic.length - 1]
        if (lastNode) statusByStep.set(lastNode, 'failed')
      }
      // Do not flip validator to success purely from events; rely on server status to avoid flicker
      if (ev.type === 'callback' || ev.type === 'started') {
        /* noop */
      }
    }

    // Also reflect server-reported current/next when available
    try {
      const cs = status?.current_step
      if (cs) statusByStep.set(cs, 'running')
      if (status?.status === 'PAUSED' && snapshot?.pending_human_request) {
        statusByStep.set('human_review', 'paused')
      }
    } catch {}
    // Compute finish/completion/failure flags
    const hasCompletedEvent = (() => {
      for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i]
        if (ev.type === 'completed') return true
        if (ev.type === 'validated') {
          const d = String(ev.done || ev.payload?.done || 'false')
          if (d === 'true') return true
        }
      }
      return false
    })()
    const hasFailureEvent = events.some(ev => ev.type === 'failed' || ev.type === 'routing_error')
    const hadHistoryErrors = errorByStep.size > 0
    const isFinished = !!(status && status.done)
    // Trust server status for completion to avoid premature green
    const isCompleted = !!(status && status.status === 'COMPLETED')
    const isFailed = (status && status.status === 'FAILED') || hasFailureEvent || (hadHistoryErrors && !isCompleted)
    // Determine current step with refined priority
    const currentStep = (() => {
      // 0) If workflow is finished (completed or failed), no active step
      if (isFinished) return null
      // 1) If there is a pending human review, prefer it regardless of paused flag
      try {
        if (snapshot?.pending_human_request) return 'human_review'
      } catch {}
      // 2) Prefer explicit server-reported current_step
      if (status?.current_step) return status.current_step
      // 3) Then use server-reported next_agent/current_step (status)
      try {
        const shint = status?.next_agent
        if (shint && shint !== 'END') return shint
      } catch {}
      // 4) Then use snapshot hint (may be slightly stale)
      try {
        const hint = snapshot?.next_agent
        if (hint && hint !== 'END') return hint
      } catch {}
      // 4) Fallback to last explicit agent_start event
      for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i]
        if (ev.type === 'agent_start' && ev.agent) return ev.agent
      }
      // 5) Then fallback to last router_decision event
      for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i]
        if (ev.type === 'router_decision' && ev.next_agent && ev.next_agent !== 'END') return ev.next_agent
      }
      // 6) As a last resort, pick next missing partial by ideal order
      try {
        const p = snapshot?.partials || {}
        const ideal = ['demand', 'analyst', 'designer', 'validator']
        for (const k of ideal) if (!p[k]) return k
      } catch {}
      return null
    })()

    // Build fixed order for stable rendering
    const baseOrder = ['demand', 'analyst', 'designer', 'validator']
    const humanActive = !!snapshot?.pending_human_request
    const includeHuman = hrSeenRef.current || humanActive
    const fixedOrder = includeHuman
      ? humanActive
        ? ['human_review', ...baseOrder]
        : ['demand', 'human_review', 'analyst', 'designer', 'validator']
      : baseOrder
    for (const k of fixedOrder) stepSet.add(k)
    if (currentStep) stepSet.add(currentStep)

    // Normalize: only one step may be 'running' at a time (except explicit 'paused'/'failed'/'success')
    const finalStatus = new Map(statusByStep)
    // Hard stop: if there is a pending human review, nobody else is running
    try {
      if (snapshot?.pending_human_request) {
        for (const [k, v] of finalStatus.entries()) if (v === 'running') finalStatus.set(k, 'pending')
        finalStatus.set('human_review', 'paused')
      }
    } catch {}
    // If state hints the next agent (e.g., after human_review_resolved), mark it as running
    try {
      const hint = snapshot?.next_agent
      if (!isFinished && hint && (!finalStatus.get(hint) || finalStatus.get(hint) === 'pending'))
        finalStatus.set(hint, 'running')
    } catch {}
    for (const [k, v] of finalStatus.entries()) {
      if (v === 'running' && k !== currentStep) finalStatus.set(k, 'pending')
    }
    if (currentStep && (!finalStatus.get(currentStep) || finalStatus.get(currentStep) === 'pending')) {
      finalStatus.set(currentStep, 'running')
    }
    // If the workflow is completed, mark validator green only after explicit completion events
    try {
      if (isCompleted) finalStatus.set('validator', 'success')
    } catch {}
    // If we know it's failed but didn't capture an explicit failed event, mark a sensible node as failed
    try {
      if (isFailed) {
        const lastNode = orderDynamic[orderDynamic.length - 1]
        const setFailed = node => {
          if (
            node &&
            (!finalStatus.get(node) || finalStatus.get(node) === 'pending' || finalStatus.get(node) === 'running')
          ) {
            finalStatus.set(node, 'failed')
          }
        }
        if (lastNode) {
          setFailed(lastNode)
        } else {
          // Decide which step was needed next based on partials
          const decideNeededStep = () => {
            try {
              const p = snapshot?.partials || {}
              if (p.designer) return 'validator'
              if (p.analyst) return 'designer'
              if (p.demand) return 'analyst'
              return 'demand'
            } catch {
              return 'demand'
            }
          }
          setFailed(decideNeededStep())
        }
      }
    } catch {}
    // Resolve historical paused mark if currently not paused
    try {
      if (!snapshot?.pending_human_request && status?.status !== 'PAUSED') {
        if (finalStatus.get('human_review') === 'paused') finalStatus.set('human_review', 'pending')
      }
    } catch {}

    // Mark completed steps as 'success' based on partials data
    try {
      const p = snapshot?.partials || {}
      const agentOrder = ['demand', 'analyst', 'designer', 'validator']
      
      for (const agent of agentOrder) {
        // If agent has data in partials and is not the current running step, mark as success
        if (p[agent] && agent !== currentStep) {
          const currentStatus = finalStatus.get(agent)
          // Don't override 'failed' status
          if (currentStatus !== 'failed') {
            finalStatus.set(agent, 'success')
          }
        }
      }
      
      // Mark human_review as success if it was seen but is no longer active
      if (hrSeenRef.current && !snapshot?.pending_human_request && status?.status !== 'PAUSED') {
        const hrStatus = finalStatus.get('human_review')
        if (hrStatus !== 'failed' && hrStatus !== 'running') {
          finalStatus.set('human_review', 'success')
        }
      }
    } catch {}

    // Respect fixed order for indices
    const nodes = Array.from(stepSet).map(name => ({
      id: name,
      label: name,
      status: finalStatus.get(name) || (currentStep === name ? 'running' : 'pending'),
      index: (() => {
        const i = fixedOrder.indexOf(name)
        return i >= 0 ? i : fixedOrder.length
      })(),
      attempts: attemptByStep.get(name) || 0,
      errors: errorByStep.get(name) || 0,
    }))

    return { nodes, edges, currentStep }
  }, [events, snapshot, status])

  const done = useMemo(() => {
    try {
      if (status?.done) return true
    } catch {}
    for (let i = (events || []).length - 1; i >= 0; i--) {
      const ev = events[i]
      if (ev.type === 'completed') return true
      if (ev.type === 'validated') {
        const d = String(ev.done || ev.payload?.done || 'false')
        if (d === 'true') return true
      }
    }
    return false
  }, [status, events])

  // External trigger to refresh immediately (atomic: status + events + snapshot)
  const refresh = async () => {
    try {
      const data = await getWorkflowLive(workflowId, 500)
      setStatus(data.status)
      const incoming = (data.events || []).slice().reverse().map(normalizeEvent)
      setEvents(prev => {
        const next = [...prev]
        for (const ev of incoming) {
          if (!seen.current.has(ev.id)) {
            seen.current.add(ev.id)
            next.push(ev)
          }
        }
        return next
      })
      setSnapshot(data.snapshot)
    } catch (e) {
      try {
        console.warn('[flow] forced refresh failed', e)
      } catch {}
    }
  }

  // loading: true until data fetched (for conditional content rendering)
  // showLoading: true only after 500ms delay (for spinner visibility)
  return { status, events, snapshot, nodes, edges, currentStep, done, refresh, loading, showLoading }
}
